import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./login.css";

const API_URL =
  "https://vortexnetowrknew.co.uk/php-backend/auth.php";

const LoginSignup = () => {
  const [action, setAction] = useState("login");

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [message, setMessage] = useState({
    type: "",
    text: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [strength, setStrength] = useState(0);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  /* =========================================
     PASSWORD STRENGTH
  ========================================= */

  const checkStrength = (pass) => {
    let score = 0;

    if (pass.length >= 8) score += 25;
    if (/[A-Z]/.test(pass)) score += 25;
    if (/[0-9]/.test(pass)) score += 25;
    if (/[^A-Za-z0-9]/.test(pass)) score += 25;

    setStrength(score);
  };

  const getStrengthText = () => {
    if (!password) return "";

    if (strength <= 25) return "Weak";
    if (strength <= 50) return "Fair";
    if (strength <= 75) return "Good";

    return "Strong";
  };

  /* =========================================
     SWITCH LOGIN / SIGNUP
  ========================================= */

  const switchAction = (newAction) => {
    setAction(newAction);

    setUsername("");
    setEmail("");
    setPassword("");
    setStrength(0);

    setMessage({
      type: "",
      text: "",
    });
  };

  /* =========================================
     FORM SUBMIT
  ========================================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setMessage({
      type: "",
      text: "",
    });

    const cleanUsername = username.trim();
    const cleanEmail = email.trim();

    if (
      !cleanEmail ||
      !password ||
      (action === "signup" && !cleanUsername)
    ) {
      setMessage({
        type: "error",
        text: "Please fill in all required fields.",
      });

      return;
    }

    if (action === "signup") {
      if (cleanUsername.length < 3) {
        setMessage({
          type: "error",
          text: "Username must be at least 3 characters long.",
        });

        return;
      }

      if (password.length < 8) {
        setMessage({
          type: "error",
          text: "Password must be at least 8 characters long.",
        });

        return;
      }

      if (strength < 50) {
        setMessage({
          type: "error",
          text: "Please choose a stronger password.",
        });

        return;
      }
    }

    setLoading(true);

    try {
      const res = await axios.post(
        API_URL,
        {
          action,
          username: cleanUsername,
          email: cleanEmail,
          password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 15000,
        }
      );

      const data = res.data;

      if (data.status === "success") {
        if (action === "login") {
          if (data.user) {
            localStorage.setItem(
              "user",
              JSON.stringify(data.user)
            );
          }

          setMessage({
            type: "success",
            text: "Login successful. Redirecting...",
          });

          setTimeout(() => {
            navigate("/");
            window.location.reload();
          }, 500);
        } else {
          setMessage({
            type: "success",
            text: "Account created successfully. You can now log in.",
          });

          setUsername("");
          setEmail("");
          setPassword("");
          setStrength(0);

          setTimeout(() => {
            setAction("login");
            setMessage({
              type: "",
              text: "",
            });
          }, 1500);
        }
      } else {
        setMessage({
          type: "error",
          text: data.message || "Unable to complete the request.",
        });
      }
    } catch (error) {
      console.error("Authentication error:", error);

      if (error.response?.data?.message) {
        setMessage({
          type: "error",
          text: error.response.data.message,
        });
      } else if (error.code === "ECONNABORTED") {
        setMessage({
          type: "error",
          text: "The server took too long to respond. Please try again.",
        });
      } else {
        setMessage({
          type: "error",
          text: "Unable to connect to the server. Please try again later.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">

      {/* Background decoration */}

      <div className="auth-background auth-background-one"></div>
      <div className="auth-background auth-background-two"></div>

      <div className="auth-card">

        {/* =================================
            BRAND
        ================================= */}

        <div className="auth-brand">
          <div className="auth-logo">
            <i className="fa-solid fa-bolt"></i>
          </div>

          <div>
            <h1>Vortex Network</h1>
            <span>Gaming Community</span>
          </div>
        </div>

        {/* =================================
            HEADER
        ================================= */}

        <div className="auth-header">
          <span className="auth-label">
            {action === "login"
              ? "WELCOME BACK"
              : "JOIN THE NETWORK"}
          </span>

          <h2>
            {action === "login"
              ? "Welcome back!"
              : "Create your account"}
          </h2>

          <p>
            {action === "login"
              ? "Sign in to continue to your Vortex Network account."
              : "Create an account and become part of the Vortex Network community."}
          </p>
        </div>

        {/* =================================
            TABS
        ================================= */}

        <div className="auth-tabs">
          <button
            type="button"
            className={action === "login" ? "active" : ""}
            onClick={() => switchAction("login")}
          >
            <i className="fa-solid fa-right-to-bracket"></i>
            Login
          </button>

          <button
            type="button"
            className={action === "signup" ? "active" : ""}
            onClick={() => switchAction("signup")}
          >
            <i className="fa-solid fa-user-plus"></i>
            Sign Up
          </button>
        </div>

        {/* =================================
            FORM
        ================================= */}

        <form onSubmit={handleSubmit} className="auth-form">

          {action === "signup" && (
            <div className="auth-field">

              <label htmlFor="username">
                Username
              </label>

              <div className="auth-input">
                <i className="fa-solid fa-user"></i>

                <input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  name="username"
                  autoComplete="username"
                  maxLength={30}
                  onChange={(e) =>
                    setUsername(e.target.value)
                  }
                />
              </div>

            </div>
          )}

          <div className="auth-field">

            <label htmlFor="email">
              Email Address
            </label>

            <div className="auth-input">
              <i className="fa-solid fa-envelope"></i>

              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                name="email"
                autoComplete="email"
                onChange={(e) =>
                  setEmail(e.target.value)
                }
              />
            </div>

          </div>

          <div className="auth-field">

            <label htmlFor="password">
              Password
            </label>

            <div className="auth-input password-input">
              <i className="fa-solid fa-lock"></i>

              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                name="password"
                autoComplete={
                  action === "signup"
                    ? "new-password"
                    : "current-password"
                }
                onChange={(e) => {
                  setPassword(e.target.value);

                  if (action === "signup") {
                    checkStrength(e.target.value);
                  }
                }}
              />

              <button
                type="button"
                className="password-toggle"
                onClick={() =>
                  setShowPassword(!showPassword)
                }
              >
                <i
                  className={
                    showPassword
                      ? "fa-solid fa-eye-slash"
                      : "fa-solid fa-eye"
                  }
                ></i>
              </button>
            </div>

          </div>

          {/* =================================
              PASSWORD STRENGTH
          ================================= */}

          {action === "signup" && password && (
            <div className="password-strength">

              <div className="strength-header">
                <span>Password strength</span>

                <strong>{getStrengthText()}</strong>
              </div>

              <div className="strength-track">
                <div
                  className={`strength-progress strength-${strength}`}
                  style={{
                    width: `${strength}%`,
                  }}
                ></div>
              </div>

              <div className="password-requirements">

                <span className={password.length >= 8 ? "valid" : ""}>
                  <i className="fa-solid fa-check"></i>
                  8+ characters
                </span>

                <span className={/[A-Z]/.test(password) ? "valid" : ""}>
                  <i className="fa-solid fa-check"></i>
                  Uppercase
                </span>

                <span className={/[0-9]/.test(password) ? "valid" : ""}>
                  <i className="fa-solid fa-check"></i>
                  Number
                </span>

                <span
                  className={
                    /[^A-Za-z0-9]/.test(password)
                      ? "valid"
                      : ""
                  }
                >
                  <i className="fa-solid fa-check"></i>
                  Symbol
                </span>

              </div>
            </div>
          )}

          {/* =================================
              MESSAGE
          ================================= */}

          {message.text && (
            <div
              className={`auth-message ${
                message.type === "success"
                  ? "success"
                  : "error"
              }`}
            >
              <i
                className={
                  message.type === "success"
                    ? "fa-solid fa-circle-check"
                    : "fa-solid fa-circle-exclamation"
                }
              ></i>

              <span>{message.text}</span>
            </div>
          )}

          {/* =================================
              SUBMIT
          ================================= */}

          <button
            type="submit"
            className="auth-submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                Please wait...
              </>
            ) : (
              <>
                <i
                  className={
                    action === "login"
                      ? "fa-solid fa-right-to-bracket"
                      : "fa-solid fa-user-plus"
                  }
                ></i>

                {action === "login"
                  ? "Login to Vortex"
                  : "Create Account"}
              </>
            )}
          </button>

        </form>

        {/* =================================
            SWITCH
        ================================= */}

        <div className="auth-switch">

          {action === "login" ? (
            <p>
              Don't have an account?{" "}
              <button
                type="button"
                onClick={() => switchAction("signup")}
              >
                Create one
              </button>
            </p>
          ) : (
            <p>
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => switchAction("login")}
              >
                Log in
              </button>
            </p>
          )}

        </div>

        {/* =================================
            FOOTER
        ================================= */}

        <div className="auth-footer">
          <i className="fa-solid fa-shield-halved"></i>
          Your account is protected by Vortex Network security.
        </div>

      </div>
    </div>
  );
};

export default LoginSignup;